import { useEffect, useId, useLayoutEffect, useRef, useState, type ComponentProps, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';
import { isInTopLayer } from '../../lib/modal-focus.ts';
import { cn } from '../../lib/utils.ts';
import { Icon } from './icon.tsx';

/** Stil JEDES Icon-Kreises – nur hier festgelegt (wie `TILE_TITLE_CLASS`); nie je Stelle überschreiben. */
// Redesign 31.10.2026 (N40): ruhiges Symbol ohne Ring, grau – der Wert steht im Vordergrund.
export const ICON_CIRCLE_CLASS = 'grid size-icon-circle flex-none place-items-center rounded-full text-ink-3';

/** Stil JEDES Pfeil-Knopfs – nur hier festgelegt; `end` rückt ihn nur ans Zeilenende. */
export const ARROW_LINK_CLASS =
  'grid size-icon-circle flex-none place-items-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-brand-hover pointer-coarse:size-11';

/**
 * Ruhiger Pfeil-Knopf (Opt-in, zuerst Startseite, Ticket 1.275 Sichtung HS 25.10.2026, „Entwurf N“): Kreis mit 1-px-Rand
 * `chip-line`, Fläche der Kachel, Pfeil in `ink` – damit vier Kacheln nicht vier dunkle Hauptknöpfe tragen (EK8).
 */
export const ARROW_LINK_QUIET_CLASS = cn(ARROW_LINK_CLASS, 'border border-chip-line bg-card text-ink hover:bg-tile');

/**
 * Icon-Kreis (DESIGN „Design S3“ › Formen, Change 11.10.2026): 38 px rund mit feinem Rand, Symbol in TILL-Dunkelblau (dunkel Hellblau).
 * Steht links in der Kopfzeile einer Kachel. `as="div"`: in Kacheln, deren erstes `span` die Beschriftung bleibt.
 */
export function IconCircle({ children, as: Tag = 'span' }: { children?: ReactNode; as?: 'span' | 'div' }) {
  return (
    <Tag aria-hidden className={ICON_CIRCLE_CLASS}>
      {children}
    </Tag>
  );
}

/**
 * Pfeil-Knopf (↗) rechts in der Kopfzeile einer Kachel: dunkelblau, rund, 38 px – nur wo er wirklich zu einer Seite führt
 * (DESIGN Do's and Don'ts). Bei Touch wächst die Trefferfläche auf 44 px.
 */
export function ArrowLink({ to, search, label, end, quiet }: { to: string; search?: Record<string, string>; label: string; end?: boolean; quiet?: boolean }) {
  return (
    <Link
      to={to}
      {...(search ? { search } : {})}
      aria-label={label}
      data-arrow-link=""
      className={cn(quiet ? ARROW_LINK_QUIET_CLASS : ARROW_LINK_CLASS, end && 'ml-auto')}
    >
      <Icon icon={ArrowUpRight} size="15" stroke="strong" aria-hidden />
    </Link>
  );
}

/** Titel als Tooltip, wenn er gekürzt wird (nur bei reinem Text; sonst bringt der Inhalt seinen Namen selbst mit). */
export function tileTitleTooltip(title: ReactNode): string | undefined {
  return typeof title === 'string' || typeof title === 'number' ? String(title) : undefined;
}

/**
 * Gekürzten Kachel-Titel vollständig zeigen (Ticket 264, Abnahme Tablet: „mit … gekürzt und beim Darüberfahren oder
 * Antippen vollständig angezeigt“). Die Maus bekommt den Tooltip über `title` am Element mit `truncate`. Nur wenn der
 * Text wirklich gekürzt ist, wird er zum Knopf: Antippen, Klick oder Enter/Leertaste zeigt den vollen Titel in einer
 * Blase darunter (passt sie dort nicht ins Bild, darüber); Esc, Tippen daneben, Scrollen oder Fokus weg schließt. Ungekürzt bleibt es reiner Text.
 * `measure`: das Element mit `truncate` (das Element selbst oder sein Elternteil).
 */
function useTitleReveal(text: string | undefined, measure: 'self' | 'parent') {
  const ref = useRef<HTMLElement>(null);
  const bubbleId = useId();
  const [cut, setCut] = useState(false);
  const [pos, setPos] = useState<{ x: number; top: number; bottom: number } | null>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const box = measure === 'self' ? ref.current : ref.current?.parentElement;
    if (!box || text === undefined) return;
    const check = () => setCut(box.scrollWidth > box.clientWidth + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(box);
    return () => observer.disconnect();
  }, [text, measure]);

  useLayoutEffect(() => {
    const bubble = bubbleRef.current;
    if (!pos || !bubble) return;
    const h = bubble.offsetHeight;
    const below = pos.bottom + 8;
    const y = below + h <= window.innerHeight - 8 ? below : Math.max(8, pos.top - 8 - h);
    bubble.style.top = `${y}px`;
  }, [pos]);

  useEffect(() => {
    if (!pos) return;
    const close = () => setPos(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isInTopLayer(ref.current)) close();
    };
    const onPointer = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('scroll', close, true);
    };
  }, [pos]);

  const active = cut && text !== undefined;
  const toggle = () => {
    if (pos) return setPos(null);
    const el = measure === 'self' ? ref.current : ref.current?.parentElement;
    const rect = el?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(320, window.innerWidth - 32);
    setPos({ x: Math.max(16, Math.min(rect.left, window.innerWidth - width - 16)), top: rect.top, bottom: rect.bottom });
  };
  const props = active
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-expanded': pos !== null,
        'aria-controls': pos ? bubbleId : undefined,
        'data-tile-title-cut': '',
        onClick: toggle,
        onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        },
        onBlur: () => setPos(null),
      }
    : {};
  const bubble =
    active && pos
      ? createPortal(
          <span
            ref={bubbleRef}
            id={bubbleId}
            role="tooltip"
            data-testid="kachel-titel-voll"
            className="type-meta fixed z-[var(--z-60)] w-max max-w-tip rounded-tile bg-card px-space-3 py-space-2 text-ink shadow-float"
            style={{ left: pos.x, top: pos.bottom + 8 }}
          >
            {text}
          </span>,
          document.body,
        )
      : null;
  return { ref, active, props, bubble };
}

/**
 * Stil JEDER Kachel-Überschrift (Rudolf 22./23.10.2026: wie „Finanzen“ auf der Startseite) – die Klasse steht in
 * `styles.css` (`.type-tile-title`, Token `--text-tile-title`); nie je Kachel überschreiben. Auch eigene `h2` im Kopf
 * einer Karte mit Icon-Kreis tragen genau diese Klasse.
 */
export const TILE_TITLE_CLASS = 'type-tile-title';

/**
 * Titel einer Kennzahl-Kachel klein und gedämpft (14 px, `ink-2`) in einer Reihe mit Symbolkreis und Pfeil – [RUDOLF
 * 25.10.2026], geht `TILE_TITLE_CLASS` (20/500) für Kennzahl-Kacheln vor; zuerst nur auf der Startseite (Ticket 1.275),
 * ERP-weit mit eigener Story. Stil nur in `styles.css` (`.type-kpi-title`).
 */
export const KPI_TITLE_CLASS = 'type-kpi-title';

const REVEAL_CLASS = 'cursor-pointer rounded-field focus-visible:outline-[length:var(--focus-w)] focus-visible:outline-ring';

/**
 * Gekürzter, antippbarer `TileTitle` bei Touch (Ticket 296): Box 44 px hoch (`spacing.hit-touch`) über Innenabstand oben
 * und unten (44 px minus eine Zeile, halbiert), Kürzung „…“ bleibt; mit Maus unverändert. Bewusst nicht über die
 * Zeilenhöhe: `.type-tile-title` setzt sie ohne @layer (nicht übersteuerbar) und jede Kachel-Überschrift soll dieselbe
 * Zeilenhöhe behalten (Wächter `e2e/kachel-titel-einheitlich.spec.ts`). Nur Variante `self` – `TileTitleText` ist inline
 * im `h2` und misst nur die Schrifthöhe.
 */
export const REVEAL_TOUCH_CLASS = 'pointer-coarse:py-[calc((var(--hit-touch)_-_1lh)/2)]';

/**
 * Einzeiliger Kachel-Titel als `span`/`div` mit `truncate`; gekürzt antippbar (siehe `useTitleReveal`). `kpi`: klein und
 * gedämpft für Kennzahl-Kacheln (`KPI_TITLE_CLASS`).
 */
export function TileTitle({ as: Tag = 'span', kpi = false, children }: { as?: 'span' | 'div'; kpi?: boolean; children: ReactNode }) {
  const text = tileTitleTooltip(children);
  const { ref, active, props, bubble } = useTitleReveal(text, 'self');
  return (
    <Tag
      ref={ref as never}
      title={text}
      data-tile-title={kpi ? 'kpi' : undefined}
      className={cn('min-w-0 flex-1 truncate', kpi ? KPI_TITLE_CLASS : TILE_TITLE_CLASS, active && REVEAL_CLASS, active && REVEAL_TOUCH_CLASS)}
      {...props}
    >
      {children}
      {bubble}
    </Tag>
  );
}

/** Kachel-Überschrift als `h2` im zentralen Stil (für Kacheln, die semantisch eine Überschrift brauchen); `kpi`: kleine Fassung wie `TileTitle kpi`. */
export function TileHeading({ id, kpi = false, children }: { id?: string; kpi?: boolean; children: ReactNode }) {
  return (
    <h2 id={id} data-tile-title={kpi ? 'kpi' : undefined} className={cn('min-w-0 flex-1 truncate', kpi ? KPI_TITLE_CLASS : TILE_TITLE_CLASS)} title={tileTitleTooltip(children)}>
      <TileTitleText>{children}</TileTitleText>
    </h2>
  );
}

/** Inhalt einer Überschrift mit `truncate` (`h2` bleibt Überschrift, der Knopf ist das innere `span`). */
export function TileTitleText({ children }: { children: ReactNode }) {
  const { ref, active, props, bubble } = useTitleReveal(tileTitleTooltip(children), 'parent');
  return (
    <span ref={ref as never} className={active ? REVEAL_CLASS : undefined} {...props}>
      {children}
      {bubble}
    </span>
  );
}

/**
 * Kachel (DESIGN `components.tile`, Change 11.10.2026 Design S3; kompakt seit Baustein D, Ticket 264): weiße Karte
 * Radius 30 ohne Rand mit sehr weichem Schatten. Kopfzeile in einer Reihe wie Entwurf N (Ticket 1.294): Icon-Kreis · kleiner
 * gedämpfter Titel (`TileTitle kpi`, einzeilig, gekürzt mit Tooltip) · optional der ruhige Pfeil-Knopf rechts; darunter Inhalt und Fußzeile (z. B. ein Kapselbalken außerhalb von
 * Kennzahlen; Kennzahl-Kacheln nutzen `KpiTile` mit Mini-Säulen und Text-Fußzeile, Ticket 211). Die Fläche kommt aus
 * `card-box`, Innenabstand oben/unten und Zeilenabstand aus den Kachel-Tokens (`tile-pad-y`, `tile-gap`).
 */
export function Tile({
  icon,
  title,
  arrow,
  footer,
  children,
  className,
  ...props
}: Omit<ComponentProps<'article'>, 'title'> & {
  icon?: ReactNode;
  title?: ReactNode;
  /** Pfeil-Knopf zur zugehörigen Seite; weglassen, wenn die Kachel nirgends hinführt. */
  arrow?: { to: string; search?: Record<string, string>; label: string };
  footer?: ReactNode;
}) {
  return (
    <article data-tile="" className={cn('card-box flex min-w-0 flex-col justify-between gap-tile-gap', className)} {...props}>
      {icon || title || arrow ? (
        <div className="flex min-w-0 items-center gap-space-3">
          {icon ? <IconCircle as="div">{icon}</IconCircle> : null}
          {title ? (
            <TileTitle as="div" kpi>
              {title}
            </TileTitle>
          ) : null}
          {arrow ? <ArrowLink to={arrow.to} {...(arrow.search ? { search: arrow.search } : {})} label={arrow.label} quiet end /> : null}
        </div>
      ) : null}
      {children != null && children !== false ? <div className="min-w-0">{children}</div> : null}
      {footer ? <div className="flex flex-col gap-space-2">{footer}</div> : null}
    </article>
  );
}
